import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    SelectItem,
    Select,
    Autocomplete,
    AutocompleteItem,
} from '@nextui-org/react';

import { v4 as uuidv4 } from 'uuid';

import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Metadata, MetadataDefinition, UploadKnowledgeArticlePayload, useCreateKnowledgeArticleMutation, useGetMetadataDefinitionsQuery, useUploadKnowledgeArticleMutation } from '@circuitry-ai/doc-data';
import {
    checkType,
    CtyButton,
    CtyShowToast,
    fileStatusValues,
    RichTextField,
    KnowledgeFileUpload,
    ListValueSelect,
} from '../../index';
import { useHasApplicationResource } from './useCheckApplicationResource';

const hasValue = (value: string | string[] | undefined | null): boolean =>
    Array.isArray(value) ? value.length > 0 : !!(value && value.trim());

// Pins already-selected values into the option list even if they've since been removed from (or never matched) the definition's catalog, so previously saved data still renders instead of appearing blank.
const getListOptions = (definitionOptions: string[] | null | undefined, currentValue: string | string[]): string[] => {
    const baseOptions = definitionOptions ?? [];
    const selectedValues = Array.isArray(currentValue) ? currentValue : [];
    const missing = selectedValues.filter((v) => !baseOptions.includes(v));
    return missing.length > 0 ? [...baseOptions, ...missing] : baseOptions;
};

export enum ArticleType {
    EXPERT_ANSWER = 'expert_answer',
    KNOWLEDGE_ARTICLE = 'knowledge_article'
}

const articleTypeMap = {
    [ArticleType.EXPERT_ANSWER]: 'Expert Answer',
    [ArticleType.KNOWLEDGE_ARTICLE]: 'Knowledge Article'
}

// Reusable form field component
const FormField = ({
    label,
    placeholder,
    value,
    onChange,
    error,
    isDisabled = false,
    isRequired = false,
    id
}: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    id: string;
}) => (
    <div className="flex flex-col gap-2 w-full">
        <label className="text-sm text-default-600 font-medium" htmlFor={id}>
            {label}{isRequired && <span className="text-danger">*</span>}
        </label>
        <Input
            type="text"
            placeholder={placeholder}
            variant="bordered"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            isInvalid={!!error}
            errorMessage={error}
            className={isDisabled ? 'read-only-component' : ''}
            classNames={{
                input: "placeholder:text-default-400"
            }}
            id={id}
        />
    </div>
);

// Reusable metadata field component
const MetadataField = ({
    item,
    index,
    onNameChange,
    onValueChange,
    onDelete,
    errors,
    isDisabled = false,
    attributeOptions,
    definition
}: {
    item: { name: string; value: string | string[]; id: string };
    index: number;
    onNameChange: (index: number, value: string) => void;
    onValueChange: (index: number, value: string | string[]) => void;
    onDelete: (index: number) => void;
    errors: { [key: string]: string };
    isDisabled?: boolean;
    attributeOptions: { name: string }[];
    definition?: MetadataDefinition;
}) => (
    <div className={`flex gap-2 ${index > 0 ? '' : 'w-[93%]'}`}>
        <Autocomplete
            size="sm"
            variant="bordered"
            placeholder="Attribute type"
            aria-label="Attribute type"
            selectedKey={item.name || null}
            onSelectionChange={(key) => onNameChange(index, key ? String(key) : '')}
            isInvalid={!!errors[`name${index}`]}
            errorMessage={errors[`name${index}`]}
            isDisabled={isDisabled}
        >
            {attributeOptions.map((def) => (
                <AutocompleteItem key={def.name} textValue={def.name}>
                    {def.name}
                </AutocompleteItem>
            ))}
        </Autocomplete>
        {definition?.type === 'list' ? (
            <ListValueSelect
                key={item.name}
                options={getListOptions(definition?.value, item.value)}
                value={Array.isArray(item.value) ? item.value : []}
                onChange={(values) => onValueChange(index, values)}
                placeholder="Attribute value"
                ariaLabel="Attribute value"
                isInvalid={!!errors[`value${index}`]}
                errorMessage={errors[`value${index}`]}
                isDisabled={isDisabled || !item.name}
            />
        ) : (
            <Input
                key={item.name}
                size="sm"
                type={definition?.type === 'number' ? 'number' : 'text'}
                placeholder="Attribute value"
                variant="bordered"
                value={typeof item.value === 'string' ? item.value : ''}
                onChange={(e) => onValueChange(index, e.target.value)}
                isInvalid={!!errors[`value${index}`]}
                errorMessage={errors[`value${index}`]}
                isDisabled={isDisabled}
            />
        )}
        {index > 0 && (
            <CtyButton
                isIconOnly
                color="danger"
                variant="light"
                onPress={() => onDelete(index)}
                isDisabled={isDisabled}
            >
                <Icon
                    icon="hugeicons:delete-02"
                    className="cursor-pointer"
                    color="red"
                    height={20}
                    width={20}
                />
            </CtyButton>
        )}
    </div>
);

// Reusable action button component
const ActionButton = ({
    variant = "solid",
    onPress,
    isLoading,
    isDisabled = false,
    children
}: {
    variant?: "solid" | "bordered";
    onPress: () => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    children: React.ReactNode;
}) => (
    <CtyButton
        variant={variant}
        onPress={onPress}
        isLoading={isLoading}
        isDisabled={isDisabled}
        className={variant === "bordered" ? "border-1 border-default-300" : ""}
        color={variant === "solid" ? "primary" : undefined}
    >
        {children}
    </CtyButton>
);

interface ArticleModelProps {
    onClose: () => void;
    articleData: Record<string, any>;
    refetch?: () => void;
    readOnly?: boolean;
}

const ArticleModel = (props: ArticleModelProps) => {
    const { onClose, articleData, refetch, readOnly = false } = props;
    const type: ArticleType = articleData?.article_type;
    const isExpertAnswer = type === ArticleType.EXPERT_ANSWER;
    const hasExpertAnswerView = useHasApplicationResource('expert_answer');
    const hasExpertAnswerPublish = useHasApplicationResource('expert_answer_publish');
    const hasExpertAnswerSaveAndSubmit = useHasApplicationResource('expert_answer_save_draft_and_submit');
    // View with no write actions matches the knowledge-article read-only modal.
    const isViewOnlyExpertAnswer = isExpertAnswer && hasExpertAnswerView && !hasExpertAnswerPublish && !hasExpertAnswerSaveAndSubmit;
    const isReadOnly = readOnly || isViewOnlyExpertAnswer;

    const [createKnowledgeArticle] = useCreateKnowledgeArticleMutation();
    const [uploadKnowledgeArticle] = useUploadKnowledgeArticleMutation();
    const { data: metadataDefinitionsData } = useGetMetadataDefinitionsQuery(undefined, { refetchOnMountOrArgChange: true });

    const [publishLoadingComponent, setPublishLoadingComponent] = useState<('drafted' | 'pending' | 'published' | null)>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const [knowledgeArticleInputData, setKnowledgeArticleInputData] = useState<Record<string, any>>(articleData);

    const handleInputChange = (field: 'question' | 'answer' | 'name', value: string) => {
        setKnowledgeArticleInputData((prevData) => ({
            ...prevData,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const articleTypeList = [
        'Expert Answer',
        'Knowledge Article'
    ]

    const activeAttributeDefinitions = useMemo(
        () => (metadataDefinitionsData?.data ?? []).filter((def: MetadataDefinition) => !def.status || def.status === 'active'),
        [metadataDefinitionsData]
    );

    const attributeDefinitionsByName = useMemo(
        () => new Map((metadataDefinitionsData?.data ?? []).map((def: MetadataDefinition) => [def.name, def])),
        [metadataDefinitionsData]
    );

    const usedAttributeNames = useMemo(
        () => new Set((knowledgeArticleInputData?.metadata ?? []).map((item: Metadata) => item.name).filter(Boolean)),
        [knowledgeArticleInputData?.metadata]
    );

    const getAttributeOptionsForRow = (currentName: string) => {
        const options = activeAttributeDefinitions.filter(
            (def: MetadataDefinition) => def.name === currentName || !usedAttributeNames.has(def.name)
        );
        if (currentName && !options.some((def: MetadataDefinition) => def.name === currentName)) {
            return [{ name: currentName }, ...options];
        }
        return options;
    };

    const addMetadataField = () => {
        if (knowledgeArticleInputData.metadata.length < 5) {
            setKnowledgeArticleInputData((prevData) => ({
                ...prevData,
                metadata: [...prevData.metadata, { name: '', value: '', id: uuidv4() }],
            }));
        }
    };

    const isAddAnotherEnabled = () => {
        const lastIndex = knowledgeArticleInputData?.metadata?.length - 1;
        const lastItem = knowledgeArticleInputData?.metadata?.[lastIndex];
        return (
            hasValue(lastItem?.name) &&
            hasValue(lastItem?.value) &&
            knowledgeArticleInputData?.metadata?.length < 5
        );
    };

    const handleMetadataChange = (
        index: number,
        field: 'name' | 'value',
        value: string | string[]
    ) => {
        const newMetadata = [...knowledgeArticleInputData.metadata];
        const previousItem = newMetadata[index];

        if (field === 'name') {
            const newDefinition = attributeDefinitionsByName.get(value as string);
            newMetadata[index] = {
                ...previousItem,
                name: value as string,
                value: newDefinition?.type === 'list' ? [] : '',
            };
        } else {
            newMetadata[index] = {
                ...previousItem,
                value,
            };
        }

        setKnowledgeArticleInputData((prevData) => ({
            ...prevData,
            metadata: newMetadata,
        }));
        validateField(index, field, value);

        if (errors[`${field}${index}`]) {
            const newErrors = { ...errors };
            delete newErrors[`${field}${index}`];
            setErrors(newErrors);
        }
    };

    const deleteMetadataField = (index: number) => {
        if (index > 0) {
            const newMetadata = knowledgeArticleInputData?.metadata?.filter(
                (_: any, i: number) => i !== index
            );
            setKnowledgeArticleInputData((prevData) => ({
                ...prevData,
                metadata: newMetadata,
            }));
            const newErrors = { ...errors };
            delete newErrors[`name${index}`];
            delete newErrors[`value${index}`];
            setErrors(newErrors);
        }
    };

    const validateField = (
        index: number,
        field: 'name' | 'value',
        value: string | string[]
    ) => {
        const newErrors = { ...errors };
        const otherField = field === 'name' ? 'value' : 'name';
        const otherValue = knowledgeArticleInputData?.metadata?.[index]?.[otherField];

        if (hasValue(value) || hasValue(otherValue)) {
            if (hasValue(value)) {
                delete newErrors[`${field}${index}`];
            } else {
                newErrors[`${field}${index}`] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required when ${otherField} is filled`;
            }
        } else {
            delete newErrors[`${field}${index}`];
            delete newErrors[`${otherField}${index}`];
        }
        setErrors(newErrors);
    };

    const validateMetadata = (newErrors: { [key: string]: string }) => {
        if (!knowledgeArticleInputData?.metadata) return;

        for (const [index, item] of knowledgeArticleInputData.metadata.entries()) {
            if (hasValue(item.name) || hasValue(item.value)) {
                if (!hasValue(item.name)) {
                    newErrors[`name${index}`] = 'Type is required when Value is filled';
                }
                if (!hasValue(item.value)) {
                    newErrors[`value${index}`] = 'Value is required when Type is filled';
                }
            }
        }
    };

    const validateAllFields = () => {
        const newErrors: { [key: string]: string } = {};

        if (!knowledgeArticleInputData.question.trim()) {
            newErrors.question = 'Question is required';
        }
        if (!knowledgeArticleInputData.answer.trim()) {
            newErrors.answer = 'Answer is required';
        }

        validateMetadata(newErrors);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    type ChangedFields = Partial<{
        question: string;
        answer: string;
        name: string;
        status: string;
        metadata: Metadata[];
    }>;

    const getChangedFields = (articleStatus: string): ChangedFields | null => {
        if (!articleData) return null;
        const changedFields: ChangedFields = {};

        if (knowledgeArticleInputData.question !== articleData.question) {
            changedFields.question = knowledgeArticleInputData.question;
        }
        if (knowledgeArticleInputData.answer !== articleData.answer) {
            changedFields.answer = knowledgeArticleInputData.answer;
        }

        if (type === ArticleType.KNOWLEDGE_ARTICLE && knowledgeArticleInputData.name !== articleData.name) {
            changedFields.name = knowledgeArticleInputData.name;
        }

        if (articleStatus !== articleData.article_status) {
            changedFields.status = articleStatus;
        }

        const filteredMetaData = knowledgeArticleInputData.metadata.filter((item: Metadata) => hasValue(item.name) && hasValue(item.value));
        const originalMetadata = articleData?.metadata ?? [];

        if (JSON.stringify(filteredMetaData) !== JSON.stringify(originalMetadata)) {
            changedFields.metadata = filteredMetaData;
        }

        return Object.keys(changedFields).length > 0 ? changedFields : null;
    };

    const handlePublish = async (articleStatus: 'published' | 'drafted' | 'pending') => {
        if (isExpertAnswer) {
            const allowed = (articleStatus === 'published' && hasExpertAnswerPublish)
                || ((articleStatus === 'drafted' || articleStatus === 'pending') && hasExpertAnswerSaveAndSubmit);
            if (!allowed) return;
        }
        try {
            setPublishLoadingComponent(articleStatus);
            if (validateAllFields()) {
                let payLoad;

                const isCreateMode = !knowledgeArticleInputData?.article_id;

                if (isCreateMode) {
                    payLoad = {
                        advisor_id: knowledgeArticleInputData.advisor_id,
                        question: knowledgeArticleInputData.question,
                        answer: knowledgeArticleInputData.answer,
                        status: articleStatus,
                        ...(type === ArticleType.KNOWLEDGE_ARTICLE && { name: knowledgeArticleInputData.name }),
                        metadata: knowledgeArticleInputData.metadata,
                        type: knowledgeArticleInputData.article_type,
                        sender_id: knowledgeArticleInputData.sender_id,
                        message_id: knowledgeArticleInputData.message_id,
                    }
                } else {
                    payLoad = getChangedFields(articleStatus);
                    if (!payLoad) {
                        CtyShowToast(checkType.info, 'No changes detected');
                        return;
                    }
                }

                isCreateMode ? await createKnowledgeArticle(payLoad as UploadKnowledgeArticlePayload).unwrap() : await uploadKnowledgeArticle({ article_id: knowledgeArticleInputData?.article_id, payload: payLoad as any }).unwrap();
                CtyShowToast(checkType.success, `${articleTypeMap[type]} ${articleStatus} Successfully`);
                refetch?.();
                onClose();
            }
        } catch (error) {
            onClose();
            console.log(error);
            CtyShowToast(checkType.error, `${articleTypeMap[type]} ${articleStatus} Failed`);
        } finally {
            setPublishLoadingComponent(null);
        }
    };

    const isStatusLocked =
        knowledgeArticleInputData?.article_status === 'active' ||
        knowledgeArticleInputData?.article_status === 'in_progress';
    const isFormDisabled = isReadOnly || isStatusLocked;

    const permissionDeniedTitle = 'Additional permission is required for this action';
    const statusLockedTitle = 'This action is not available while the article is in its current status';

    const saveDraftDisabled =
        isExpertAnswer
            ? !hasExpertAnswerSaveAndSubmit || isFormDisabled
            : isFormDisabled;
    const submitDisabled = saveDraftDisabled;
    const publishDisabled = isExpertAnswer ? !hasExpertAnswerPublish : false;

    const saveDraftDisabledTitle = isExpertAnswer && !hasExpertAnswerSaveAndSubmit
        ? permissionDeniedTitle
        : isFormDisabled && !isReadOnly
            ? statusLockedTitle
            : undefined;
    const submitDisabledTitle = saveDraftDisabledTitle;
    const publishDisabledTitle = isExpertAnswer && !hasExpertAnswerPublish
        ? permissionDeniedTitle
        : undefined;

    // View-only Expert Answer still shows workflow buttons (disabled) so permissions are visible.
    const showWorkflowFooter = !readOnly || isViewOnlyExpertAnswer;

    return (
        <Modal
            isOpen={!!articleData}
            onClose={onClose}
            className={`max-w-[none] article-modal-container sm:w-[80%] md:w-[80%] lg:w-[60%] h-[90%] `}
        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <div className='flex justify-start gap-3 items-center'>
                                <span>{articleTypeMap[type]}</span>
                            </div>
                            <p className="text-sm text-default-500 font-normal">
                                {isReadOnly
                                    ? `View ${articleTypeMap[type]}`
                                    : `Create an ${articleTypeMap[type]} for your advisor`}
                            </p>
                        </ModalHeader>

                        <ModalBody className='overflow-y-auto'>
                            <div className="flex flex-col gap-4">
                                <Select
                                    className='read-only-component'
                                    placeholder="Select Article Type"
                                    selectedKeys={new Set([articleTypeMap[type]])}
                                    id='article_type'
                                    disallowEmptySelection
                                    aria-label='article_type'
                                >
                                    {articleTypeList.map((item) => (
                                        <SelectItem value={item} key={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </Select>

                                {knowledgeArticleInputData?.article_status && (
                                    <FormField
                                        label="Status"
                                        placeholder="Status"
                                        value={fileStatusValues[knowledgeArticleInputData?.article_status as keyof typeof fileStatusValues]}
                                        onChange={() => undefined}
                                        id="article_status"
                                        isDisabled={true}
                                    />
                                )}

                                <div className="flex gap-4">
                                    <FormField
                                        label="Author"
                                        placeholder="Author"
                                        value={knowledgeArticleInputData?.author}
                                        onChange={() => undefined}
                                        isDisabled={true}
                                        id="article_author"
                                    />
                                    <FormField
                                        label="Upload date"
                                        placeholder=""
                                        value={knowledgeArticleInputData?.upload_date}
                                        onChange={() => undefined}
                                        isDisabled={true}
                                        id="upload-date"
                                    />
                                </div>

                                {type === ArticleType.KNOWLEDGE_ARTICLE && (
                                    <FormField
                                        label="Topic title"
                                        placeholder="name"
                                        value={knowledgeArticleInputData.name}
                                        onChange={(value) => handleInputChange('name', value)}
                                        id='name'
                                        isRequired={false}
                                        isDisabled={isFormDisabled}
                                    />
                                )}

                                <RichTextField
                                    label="Subject or Question"
                                    value={knowledgeArticleInputData.question}
                                    onChange={(value) => handleInputChange('question', value)}
                                    error={errors.question}
                                    isDisabled={isFormDisabled}
                                    isRequired={true}
                                    id="article_question"
                                    initialValue={articleData?.question}
                                    placeholder={'Start typing...'}
                                />

                                <RichTextField
                                    label="Answer"
                                    value={knowledgeArticleInputData.answer}
                                    onChange={(value) => handleInputChange('answer', value)}
                                    error={errors.answer}
                                    isDisabled={isFormDisabled}
                                    isRequired={true}
                                    id="article_answer"
                                    initialValue={articleData?.answer}
                                    placeholder={'Start typing...'}
                                />

                                <div className="text-xs text-default-600 mt-1">
                                    Last edited by {knowledgeArticleInputData?.author}
                                </div>

                                {!isReadOnly && (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            <span className="text-base font-semibold">Upload Files</span>
                                        </div>
                                        {knowledgeArticleInputData?.advisor_id && (
                                            <KnowledgeFileUpload
                                                advisorID={knowledgeArticleInputData.advisor_id}
                                                uploadAreaId={knowledgeArticleInputData.advisor_id + '-advisor-knowledgeupload-article'}
                                            />
                                        )}
                                    </>
                                )}

                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col">
                                        <span className="text-base font-semibold">Metadata</span>
                                        {!isReadOnly && (
                                            <span className="text-sm text-default-600 font-normal">
                                                Instructional copy can go here
                                            </span>
                                        )}
                                    </div>
                                    {knowledgeArticleInputData?.metadata?.map((item: Metadata, index: number) => (
                                        <MetadataField
                                            key={item.id || `metadata-${index}`}
                                            item={item}
                                            index={index}
                                            onNameChange={(index, value) => handleMetadataChange(index, 'name', value)}
                                            onValueChange={(index, value) => handleMetadataChange(index, 'value', value)}
                                            onDelete={deleteMetadataField}
                                            errors={errors}
                                            isDisabled={isFormDisabled}
                                            attributeOptions={getAttributeOptionsForRow(item.name)}
                                            definition={attributeDefinitionsByName.get(item.name)}
                                        />
                                    ))}
                                    {!isFormDisabled && knowledgeArticleInputData?.metadata?.length < 5 && (
                                        <CtyButton
                                            onPress={addMetadataField}
                                            isDisabled={!isAddAnotherEnabled()}
                                            startContent={<Icon icon="fa-solid:plus" />}
                                            variant="light"
                                            className={`w-[max-content] border-1 border-default-300 text`}
                                        >
                                            Add Another
                                        </CtyButton>
                                    )}
                                </div>
                            </div>
                        </ModalBody>

                        {showWorkflowFooter ? (
                            <ModalFooter>
                                <span title={saveDraftDisabled ? saveDraftDisabledTitle : undefined} className="inline-flex">
                                    <ActionButton
                                        variant="bordered"
                                        onPress={() => handlePublish('drafted')}
                                        isLoading={publishLoadingComponent === 'drafted'}
                                        isDisabled={saveDraftDisabled}
                                    >
                                        Save Draft
                                    </ActionButton>
                                </span>
                                <span title={submitDisabled ? submitDisabledTitle : undefined} className="inline-flex">
                                    <ActionButton
                                        variant="solid"
                                        onPress={() => handlePublish('pending')}
                                        isLoading={publishLoadingComponent === 'pending'}
                                        isDisabled={submitDisabled}
                                    >
                                        Submit
                                    </ActionButton>
                                </span>
                                <span title={publishDisabled ? publishDisabledTitle : undefined} className="inline-flex">
                                    <ActionButton
                                        variant="solid"
                                        onPress={() => handlePublish('published')}
                                        isLoading={publishLoadingComponent === 'published'}
                                        isDisabled={publishDisabled}
                                    >
                                        Publish
                                    </ActionButton>
                                </span>
                            </ModalFooter>
                        ) : null}
                    </>
                )}
            </ModalContent>
        </Modal>
    )
}

export { ArticleModel };